import moment from 'moment';

const parseCouponPromoText = (action, computedTriggerLabel,
    computedBaseOn,
    couponLimit,
    computedTriggerAmount,
    couponPrintAsOne = false, promoText) => {
    // params: <couponPrintAsOne>,<computedTriggerAmount>,<datenow>,<computedBaseOn>,N,0,<couponLimit>,A
    // ex: PRINTONCE,200,05/03/2026,S,N,0,2,A

    // <computedTriggerAmount>,<datenow>,<computedBaseOn>,N,0,<couponLimit>,A

    try {
        if (action === 'create') {
            let promoText = ''
            if (computedTriggerLabel === 'for_every' && couponPrintAsOne) promoText += 'PRINTONCE,'
            promoText += `${computedTriggerAmount || 0}`
            promoText += `,${moment().format("YYYY-MM-DD")}`
            promoText += `,${computedBaseOn === 'selected_items_amount' ? 'S' : 'R'}`
            promoText += `,N` // Fixed
            promoText += `,0` // Fixed
            promoText += `,${computedTriggerLabel === 'for_every' ? couponLimit : 0}`
            promoText += `,A`
            return promoText
        } else if (action === 'update') {
            const promoTextArr = promoText.split(',');
            if (promoTextArr.length > 0) {
                if (promoText.includes("PRINTONCE")) {
                    return {
                        computed_coupon_limit: promoTextArr[6],
                        computed_coupon_base_on: promoTextArr[3] === 'S' ? 'selected_items_amount' : 'receipt_amount',
                        computed_coupon_print_as_one: true,
                        computed_trigger_amount: promoTextArr[1],
                    }
                } else {
                    return {
                        computed_coupon_limit: 0,
                        computed_coupon_base_on: promoTextArr[2] === 'S' ? 'selected_items_amount' : 'receipt_amount',
                        computed_coupon_print_as_one: false,
                        computed_trigger_amount: promoTextArr[0],
                    }
                }
            } else {
                throw new Error('Invalid promo text.')
            }
        } else {
            throw new Error('Action undefined.')
        }
    } catch (e) {
        console.log(e.message)
        return null
    }
}

const formatCurrency = (value) => {
    // Treat null/undefined and empty/whitespace strings as 0. Without the
    // empty-string guard, parseFloat('') is NaN and NaN.toFixed(2) returns the
    // string "NaN", which leaked into the ROUND column and failed validation.
    if (value == null || String(value).trim() === '' || isNaN(value)) return "0"

    let num = parseFloat(value)

    if (isNaN(num)) return "0"

    if (Number.isInteger(num)) return num.toString()

    return parseFloat(num.toFixed(2)).toString()
}

const lineRoundToNearest = (action, description = null) => {
    try {
        if (action === 'create') {

            description = description && description.trim() !== '' ? Number(description) : 0

            if (!_.isEmpty(description) || parseFloat(description) > 0) {
                return {
                    text: `^${formatCurrency(description)}`,
                    desc: ` Round up ${formatCurrency(description)}`,
                }
            }
            return { text: '', desc: '' }
        } else if (action === 'update') {
            const part = description.split(':')[2] || ''
            const match = part.match(/\^([\d.]+)/)
            // No "^" round-up marker means "no rounding" => 0, not blank/NaN.
            const roundUpVal = match ? match[1] : '0'
            return { text: formatCurrency(roundUpVal), desc: '' }
        } else {
            throw new Error('Action undefined.')
        }
    } catch (e) {
        console.log(e.message)
        return { text: '', desc: '' }
    }
}

const extractDetailPromoText = (promoType = 'GROUPBONUS', rewardSymbol = 'PERCENTAGE', description = '') => {
    try {
        if (promoType === 'GROUPBONUS') {
            let discountType = '';
            let exponentMatch = 0;

            const promoMatch = description.split(";")[0].split(":")[1].split("^")

            if(rewardSymbol == '%') {
                discountType = promoMatch[0].replace("%","")
                exponentMatch = promoMatch[1]
            } else if (rewardSymbol == 'L') {
                discountType = promoMatch[0].replace("L","")
                exponentMatch = promoMatch[1]
            } else if (rewardSymbol == 'F') {
                discountType = promoMatch[0].replace("F","")
                exponentMatch = promoMatch[1]
            }

            return {
                discountType: discountType,
                exponent: exponentMatch
            }
        }

        throw new Error(`Unknown Type: ${promoType}`)
    } catch (e) {
        console.log(e.message)
        return { discountType: '', exponentMatch: 0 }
    }
}

// -----------------------------------------------------------------------------
// Coupon Promo (a.k.a. "Coupon Stub") helpers.
//
// A Coupon Promo lists the participating items in the promotion; it does NOT
// define a discount amount. Therefore the Discount field must be hidden and the
// Discount validation must be skipped for Coupon Promo. The Promo Trigger Amount
// (computed_trigger_amount) and Reward Amount (reward_trigger_amount) remain
// required.
//
// Coupon Promo is identified by promo_type '5' (labelled "Coupon Stub" in the
// PromotionFormV2 promo-type map). These helpers are pure so the rules can be
// unit-tested in isolation and reused by the component, keeping the UI, save
// payload, and validation in agreement.
// -----------------------------------------------------------------------------
const COUPON_PROMO_TYPE = '5';

// True when the given promo type is a Coupon Promo.
const isCouponPromo = (promoType) => String(promoType) === COUPON_PROMO_TYPE;

// Coupon Promo never requires a Discount value. Every other promo type keeps
// its existing discount requirement (this helper only answers the coupon case).
const couponPromoRequiresDiscount = (promoType) => !isCouponPromo(promoType);

// Whether the shared "Percent/Amount Discount" field is shown. Mirrors the
// existing template condition (`include_item <= 5 && !['5'].includes(promo_type)`)
// so Coupon Promo never exposes the Discount field, while all other promo types
// are unaffected.
const shouldShowMainDiscountField = (promoType, includeItem) => {
    const includeItemValue = Number(includeItem);
    const withinNonSpecificRange = !Number.isNaN(includeItemValue) && includeItemValue <= 5;
    return withinNonSpecificRange && !isCouponPromo(promoType);
};

// Whether the per-product "PERCENT DISCOUNT" column (promo_value) is shown/editable
// in the selected-products table AND whether a per-product discount value is
// required. Coupon Promo only lists participating items, so it neither shows the
// column nor requires a value. Every other promo type keeps its existing behaviour.
const showProductDiscountColumn = (promoType) => !isCouponPromo(promoType);
const requiresProductDiscountValue = (promoType) => !isCouponPromo(promoType);

// --- Coupon Promo: BASED ON "Receipt Amount" forces item selection "All Item" ---
//
// A Coupon Promo (promo_type '5') is BASED ON either the selected items amount
// or the whole Receipt Amount. When it is BASED ON Receipt Amount the coupon
// applies to the entire receipt, so the item selection MUST be "All Item" and
// the selection field is locked. These pure helpers encode that rule so the
// component and the unit tests stay in agreement.
const COUPON_BASE_ON_RECEIPT_AMOUNT = 'receipt_amount';
// "All Items" is key '1' in the component's includeItemOptions.
const COUPON_ALL_ITEM_KEY = '1';

// True when a coupon is BASED ON Receipt Amount (tolerant of numeric promoType).
const couponBasedOnReceiptAmount = (promoType, baseOn) =>
    isCouponPromo(promoType) && String(baseOn) === COUPON_BASE_ON_RECEIPT_AMOUNT;

// Whether the item-selection field must be locked (disabled + forced to All
// Item). Same condition as couponBasedOnReceiptAmount; named for intent at the
// call site (the :disabled binding / computed).
const couponItemSelectionLocked = (promoType, baseOn) =>
    couponBasedOnReceiptAmount(promoType, baseOn);

// The item selection value the form must hold for the given coupon basis:
// All Item ('1') when BASED ON Receipt Amount, otherwise the current value is
// kept (returns null to signal "no forced value").
const enforceCouponItemSelection = (promoType, baseOn, currentIncludeItem) => {
    if (couponBasedOnReceiptAmount(promoType, baseOn)) return COUPON_ALL_ITEM_KEY;
    return currentIncludeItem == null ? null : String(currentIncludeItem);
};

export {
    parseCouponPromoText,
    formatCurrency,
    lineRoundToNearest,
    extractDetailPromoText,
    COUPON_PROMO_TYPE,
    isCouponPromo,
    couponPromoRequiresDiscount,
    shouldShowMainDiscountField,
    showProductDiscountColumn,
    requiresProductDiscountValue,
    COUPON_BASE_ON_RECEIPT_AMOUNT,
    COUPON_ALL_ITEM_KEY,
    couponBasedOnReceiptAmount,
    couponItemSelectionLocked,
    enforceCouponItemSelection
}


// const testCouponText = parseCouponPromoText(this.promo.computed_coupon_base_on,
//     this.promo.computed_coupon_limit,
//     this.promo.computed_trigger_amount,
//     this.promo.computed_coupon_print_as_one
// )
// console.log("Promo Coupon Text:",testCouponText)
